import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { GrowthBookClient } from '@growthbook/growthbook'

const GrowthbookContext = createContext({
  isReady: true,
  getFeatureValue: () => false,
})

const buildAttributes = userId => {
  if (!userId) {
    return {}
  }

  return {
    id: userId,
    user_id: userId,
  }
}

export const GrowthbookProvider = ({
  apiHost,
  clientKey,
  userId,
  children,
}) => {
  const [client, setClient] = useState(null)
  const [isReady, setIsReady] = useState(!apiHost || !clientKey)

  useEffect(() => {
    let isMounted = true

    if (!apiHost || !clientKey) {
      setClient(null)
      setIsReady(true)
      return undefined
    }

    const nextClient = new GrowthBookClient({
      apiHost,
      clientKey,
      autoRefresh: true,
      debug: process.env.RAILS_ENV === 'development',
    })

    setClient(nextClient)
    setIsReady(false)

    nextClient
      .init({ timeout: 1000 })
      .catch(error => {
        console.error('Failed to initialize GrowthBook features:', error)
      })
      .finally(() => {
        if (isMounted) {
          setIsReady(true)
        }
      })

    return () => {
      isMounted = false
    }
  }, [apiHost, clientKey])

  const value = useMemo(
    () => ({
      isReady,
      getFeatureValue: (flagName, defaultValue = false) => {
        if (!client) {
          return defaultValue
        }

        return client.getFeatureValue(
          flagName,
          defaultValue,
          buildAttributes(userId)
        )
      },
    }),
    [client, isReady, userId]
  )

  return (
    <GrowthbookContext.Provider value={value}>
      {children}
    </GrowthbookContext.Provider>
  )
}

export const useGrowthbookFlag = flagName => {
  const { getFeatureValue } = useContext(GrowthbookContext)
  return getFeatureValue(flagName, false)
}

export const useGrowthbookStatus = () => useContext(GrowthbookContext).isReady

export const useGrowthbookFeatureValue = () =>
  useContext(GrowthbookContext).getFeatureValue
